import React, { useState } from 'react';
import { Bell, Mail, MessageSquare, Save } from 'lucide-react';

export default function NotificationSettings() {
  const [settings, setSettings] = useState({
    emailEnabled: true,
    smsEnabled: false,
    inAppEnabled: true,
    criticalThreshold: 1,
    escalationTime: 30,
    notificationChannels: {
      email: 'admin@example.com',
      sms: '',
      slack: ''
    }
  });
  const [saved, setSaved] = useState(false);

  const handleSave = () => {
    // In real implementation, save to database
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  return (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <Bell className="w-5 h-5 text-blue-600 mr-3 mt-0.5" />
          <div>
            <h3 className="text-sm font-semibold text-blue-900 mb-1">
              Super Admin Notification System
            </h3>
            <p className="text-xs text-blue-700">
              Configure how and when Super Admins receive critical error notifications. 
              Critical errors automatically trigger alerts based on these settings.
            </p>
          </div>
        </div>
      </div>
      {/* Notification Channels */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Notification Channels</h3>
        <div className="space-y-4">
          <label className="flex items-center space-x-3">
            <input
              type="checkbox"
              checked={settings?.emailEnabled}
              onChange={(e) => setSettings({ ...settings, emailEnabled: e?.target?.checked })}
              className="rounded border-gray-300"
            />
            <Mail className="w-5 h-5 text-gray-600" />
            <span className="text-sm font-medium text-gray-700">Email Notifications</span>
          </label>
          {settings?.emailEnabled && (
            <input
              type="email"
              value={settings?.notificationChannels?.email}
              onChange={(e) => setSettings({
                ...settings,
                notificationChannels: { ...settings?.notificationChannels, email: e?.target?.value }
              })}
              placeholder="admin@example.com"
              className="ml-8 w-full max-w-md border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          )}

          <label className="flex items-center space-x-3">
            <input
              type="checkbox"
              checked={settings?.smsEnabled}
              onChange={(e) => setSettings({ ...settings, smsEnabled: e?.target?.checked })}
              className="rounded border-gray-300"
            />
            <MessageSquare className="w-5 h-5 text-gray-600" />
            <span className="text-sm font-medium text-gray-700">SMS Notifications</span>
          </label>
          {settings?.smsEnabled && (
            <input
              type="tel"
              value={settings?.notificationChannels?.sms}
              onChange={(e) => setSettings({
                ...settings,
                notificationChannels: { ...settings?.notificationChannels, sms: e?.target?.value }
              })}
              placeholder="+1 (555) 123-4567"
              className="ml-8 w-full max-w-md border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          )}

          <label className="flex items-center space-x-3">
            <input
              type="checkbox"
              checked={settings?.inAppEnabled}
              onChange={(e) => setSettings({ ...settings, inAppEnabled: e?.target?.checked })}
              className="rounded border-gray-300"
            />
            <Bell className="w-5 h-5 text-gray-600" />
            <span className="text-sm font-medium text-gray-700">In-App Notifications</span>
          </label>
        </div>
      </div>
      {/* Alert Thresholds */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Alert Thresholds</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Critical Error Threshold
            </label>
            <p className="text-xs text-gray-600 mb-2">
              Send alert after this many critical errors within 1 hour
            </p>
            <input
              type="number"
              value={settings?.criticalThreshold}
              onChange={(e) => setSettings({ ...settings, criticalThreshold: parseInt(e?.target?.value) })}
              min="1"
              max="10"
              className="w-32 border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Escalation Time (minutes)
            </label>
            <p className="text-xs text-gray-600 mb-2">
              Escalate unacknowledged alerts after this time
            </p>
            <input
              type="number"
              value={settings?.escalationTime}
              onChange={(e) => setSettings({ ...settings, escalationTime: parseInt(e?.target?.value) })}
              min="5"
              max="120"
              step="5"
              className="w-32 border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>
        </div>
      </div>
      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          className="flex items-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Save className="w-4 h-4" />
          <span>{saved ? 'Saved!' : 'Save Settings'}</span>
        </button>
      </div>
    </div>
  );
}